import { BaseComponent } from './base-component.js'
import { customElement, property, state } from 'lit/decorators.js'
import { html, css, CSSResultGroup, PropertyValues, TemplateResult } from 'lit'
import { DatePickerDay, DatePickerMode } from './date-picker-component.js'
import { dateIsInThePast, formatAsYearMonthDay } from './utils/date-helpers.js'

/**
 * slots:
 *  -
 */
@customElement('il-date-picker-date')
export default class DatePickerDateComponent extends BaseComponent {
  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-date-picker-date {
        position: relative;
        display: block;
        width: 100%;
        max-width: 100%;
        height: 48px;

        padding: 0;

        background-color: var(--il-color-neutrals-0);
        border: 2px solid var(--il-color-neutrals-200);
        border-radius: 6px;
        cursor: pointer;
        outline: none;
        overflow: hidden;

        transition: border-color .2s ease, background-color .2s ease;
      }

      .il-date-picker-date.il-date-picker-date--hide-prices {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .il-date-picker-date:hover {
        border-color: var(--il-color-neutrals-250);
      }

      .il-date-picker-date:focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-date-picker-date.il-date-picker-date--disabled {
        background-color: var(--il-color-neutrals-50);
        opacity: .5;
        pointer-events: none;
      }

      .il-date-picker-date.il-date-picker-date--sold-out {
        background-color: var(--il-color-neutrals-50);
      }

      .il-date-picker-date.il-date-picker-date--sold-out:hover,
      .il-date-picker-date.il-date-picker-date--sold-out:focus-visible {
        border: 2px solid var(--il-color-neutrals-200);
      }

      .il-date-picker-date.il-date-picker-date--sold-out .il-date-picker-date__date-container,
      .il-date-picker-date.il-date-picker-date--sold-out .il-date-picker-date__price-container {
        font-weight: var(--il-font-weight-normal);
        color: var(--il-color-neutrals-250);
      }

      .il-date-picker-date.il-date-picker-date--preselected {
        border-color: var(--il-color-neutrals-350);
      }

      .il-date-picker-date.il-date-picker-date--selected {
        background-color: var(--il-color-accent-300);
        border-color: var(--il-color-accent-300);
      }

      .il-date-picker-date.il-date-picker-date--selected:hover {
        background-color: var(--il-color-accent-400);
        border-color: var(--il-color-accent-400);
      }

      .il-date-picker-date.il-date-picker-date--selected:focus-visible {
        background-color: var(--il-color-accent-400);
        border-color: var(--il-color-accent-200);
      }

      .il-date-picker-date.il-date-picker-date--selected .il-date-picker-date__date-container,
      .il-date-picker-date.il-date-picker-date--selected .il-date-picker-date__price-container {
        color: var(--il-color-neutrals-0);
      }

      .il-date-picker-date.il-date-picker-date--past {
        border: none;
        background-color: transparent;
        cursor: default;
      }

      .il-date-picker-date.il-date-picker-date--past .il-date-picker-date__date-container,
      .il-date-picker-date.il-date-picker-date--past .il-date-picker-date__price-container {
        color: var(--il-color-neutrals-250);
        font-weight: 400;
      }

      il-tooltip::part(tooltip-content),
      il-tooltip::part(tooltip-trigger) {
        width: 100%;
      }

      .il-date-picker-date__right-corner-container {
        position: absolute;
        top: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        padding: .375rem;
      }

      .il-date-picker-date__corner-circle {
        width: 5px;
        height: 5px;
        border-radius: 100px;

        background-color: var(--il-color-accent-200);
      }

      .il-date-picker-date__date-container {
        color: var(--il-color-neutrals-350);
        font-size: var(--il-font-size-16);
        font-weight: var(--il-font-weight-bold);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-date-picker-date__price-container {
        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-12);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);

        min-height: 18px;
      }
    `
  ]

  protected mode: string = DatePickerMode.DEFAULT

  @property({ attribute: 'calendar-date', type: Object })
  calendarDate!: DatePickerDay

  @property({ attribute: 'price-text-override', type: String })
  priceTextOverride?: string = undefined

  @property({ attribute: 'tooltip-text-override', type: String })
  tooltipTextOverride?: string = undefined

  @property({ attribute: 'same-price-label', type: String })
  samePriceLabel?: string = ''

  @property({ attribute: 'sold-out-tooltip-label', type: String })
  soldOutTooltipLabel?: string = 'Sold out'

  @property({ attribute: 'display-right-corner-circle', type: Boolean })
  displayRightCornerCircle: boolean = false

  // when enabled, past dates are selectable
  @property({ attribute: 'enable-past-dates', type: Boolean })
  enablePastDates: boolean = false

  // arbitrary disabled state by argument
  @property({ attribute: 'disabled', type: Boolean })
  isDisabled: boolean = false

  @property({ attribute: 'should-show-tooltip', type: Boolean })
  shouldShowTooltip: boolean = false

  @property({ attribute: 'on-calendar-date-click', type: Function })
  onClick!: (date: DatePickerDay) => void

  @property({ attribute: 'tooltip-disable-content-hovering', type: Boolean})
  tooltipDisableContentHovering?: boolean

  @property({attribute: 'hide-prices', type: Boolean})
  hidePrices: boolean = false

  @state() priceHasOverflow = false

  get formattedSimpleDate (): string {
    if (!this.calendarDate?.date) {
      return ''
    }

    return formatAsYearMonthDay(this.calendarDate.date)
  }

  get availabilityDate (): any {
    return this.calendarDate?.availabilityDate ?? undefined
  }

  get isPast (): boolean {
    if (this.enablePastDates) {
      return false
    }

    return this.calendarDate && dateIsInThePast(this.calendarDate.date)
  }

  get isSelectable (): boolean {
    return this.calendarDate?.isSelectable ?? false
  }

  get isPreselected (): boolean {
    return this.calendarDate?.isPreselected ?? false
  }

  get isSelected (): boolean {
    return this.calendarDate?.isSelected ?? false
  }

  get isSoldOut (): boolean {
    if (this.isPast) {
      return false
    }

    const hasAvailabilityDate = !!this.availabilityDate
    const isSoldOut = hasAvailabilityDate && this.availabilityDate.isSoldOut

    return !hasAvailabilityDate || isSoldOut
  }

  get price (): number {
    return parseFloat(this.availabilityDate?.price ?? 0) ?? 0
  }

  get currency (): string {
    return this.availabilityDate?.currency ?? '$'
  }

  get formattedCurrencySymbol (): string {
    return this.availabilityDate?.formattedCurrencySymbol ?? '$'
  }

  get priceToDisplay (): string {
    // allows external overrides of what we display on the price section
    if (this.priceTextOverride) {
      return this.priceTextOverride
    }

    if (this.priceHasOverflow) {
      return '$'
    }

    const price = this.price

    if (price > 0) {
      return `${this.formattedCurrencySymbol}${price.toFixed(0)}`
    }

    return ''
  }

  get tooltipText (): string {
    if (this.isSoldOut) {
      return this.soldOutTooltipLabel ?? 'Sold Out'
    }

    const price = `${this.formattedCurrencySymbol}${this.price}`

    if (this.tooltipTextOverride) {
      return this.tooltipTextOverride.replace('[price]', price)
    }

    return `Costs ${price} more`
  }

  get showTooltip (): boolean {
    return !this.isPast && this.shouldShowTooltip
  }

  get dateNumber (): number {
    if (!this.calendarDate) {
      return 0
    }

    return this.calendarDate.date.getDate()
  }

  // runs each time there's a getter/external value updated
  // be careful to change other getters here because they can trigger another updated() call
  // and create an infinite loop, so use bool flags accordingly
  override updated (_changedProperties: PropertyValues): void {
    if (!this.DOMReady && !this.isLoading) {
      this.measurePriceDOM()
    }
  }

  /**
   * checks whether price span content overflows its container
   * to display a shorter version of the price
   * @private
   */
  protected async measurePriceDOM (): Promise<void> {
    // waiting until lit rendering is done before making any DOM calculations
    await this.updateComplete

    const priceContainerDOM = this.shadowRoot?.querySelector('.il-date-picker-date__price-container')
    const priceTextDOM = this.shadowRoot?.querySelector('.il-date-picker-date__price-container span')
    const priceContainerWidth = priceContainerDOM?.getBoundingClientRect()?.width ?? 0
    const priceTextWidth = priceTextDOM?.getBoundingClientRect()?.width ?? 0

    this.priceHasOverflow = priceTextWidth > priceContainerWidth

    if (priceTextWidth > 0) {
      this.DOMReady = true
    }
  }

  _onClick (): void {
    if (!this.isSelectable || this.isPast || this.isDisabled) {
      return
    }

    this.onClick?.(this.calendarDate)
  }

  override render (): TemplateResult {
    return html`
      <button
        class="
          ${this.baseCSSClass}
          il-date-picker-date
          ${this.extraCSSClasses}
          ${this.isPast ? 'il-date-picker-date--past' : ''}
          ${!this.priceHasOverflow ? 'il-date-picker-date--price-overflow' : ''}
          ${!this.isSelectable ? 'il-date-picker-date--not-selectable' : ''}
          ${this.isPreselected ? 'il-date-picker-date--preselected' : ''}
          ${this.isSelected ? 'il-date-picker-date--selected' : ''}
          ${this.isSoldOut ? 'il-date-picker-date--sold-out' : ''}
          ${this.isDisabled ? 'il-date-picker-date--disabled' : ''}
          ${this.hidePrices ? 'il-date-picker-date--hide-prices' : ''}
        "
        data-test-iliad-date-picker-date-mode="default"
        data-test-iliad-date-picker-date="${this.formattedSimpleDate}"
        @click="${() => {this._onClick()}}"
      >
        ${this.renderRightCornerCircle()}

        ${this.renderDate()}
      </button>
    `
  }

  protected renderDate (): TemplateResult {
    let dateHtml = html`
      <div class="il-date-picker-date__date-container" part="date" data-test-iliad-date-picker-date-day-number>
        ${this.dateNumber}
      </div>

      ${!this.hidePrices ? html`
        <div class="il-date-picker-date__price-container" part="price" data-test-iliad-date-picker-date-price>
          <span>
            ${this.priceToDisplay}
          </span>
        </div>
      ` : html``}
    `

    if (this.showTooltip && (this.priceHasOverflow || this.tooltipTextOverride)) {
      dateHtml = html`
        <il-tooltip
          tooltip-text="${this.tooltipText}"
          .disable-content-hovering="${this.tooltipDisableContentHovering}"
          tooltip-position="top"
          close-timeout="5000"
        >
          <div class="il-date-picker-date__date-container" part="date" data-test-iliad-date-picker-date-day-number>
            ${this.dateNumber}
          </div>

          <div class="il-date-picker-date__price-container" part="price" data-test-iliad-date-picker-date-price>
            <span>
              ${this.priceToDisplay}
            </span>
          </div>
        </il-tooltip>
      `
    }

    return dateHtml
  }

  protected renderRightCornerCircle (): TemplateResult {
    if (this.displayRightCornerCircle) {
      return html`
        <div class="il-date-picker-date__right-corner-container">
          <div class="il-date-picker-date__corner-circle"></div>
        </div>
      `
    }

    return html``
  }
}
